import { useRef, useState } from "react";
import { PortControlPermitDocument, PortControlPermitFormState, PortControlPermitRequest } from "../types/permits";
import usePermitForm, { UsePermitFormManagerProps } from "./usePermitForm";
import useDateInput from "./useDateInput";
import { Date_isSameDateAndTime, Date_toDateTimeString } from "../common/dateUtils";
import { updateAdminCommentAsync, updatePermitAsync, updatePositionsAsync } from "../apiClient";
import { CodeStatus, SignatureType } from "../common/enums";
import { showDangerDialog, showSuccessAlert } from "../common";
import { Signature_empty } from "../types/Signature";
import useSignature from "./useSignature";
import { PositionPickerRow_toPosition } from "../types/forms/Permits/PositionPickerRow";
import AnonymousPermitRequest from "../types/requests/AnonymousPermitRequest";

function usePortControlPermitForm<
    TPermitDocument extends PortControlPermitDocument,
    TPermitForm extends PortControlPermitFormState,
    TPermitRequest extends PortControlPermitRequest
>(props: UsePermitFormManagerProps<TPermitDocument, TPermitForm, TPermitRequest>) {
    const [showUpdateWithNotificationModal, setShowUpdateWithNotificationModal] = useState(false);

    const baseFormManager = usePermitForm(props);

    const { storedPermit, formState, handleValidationErrors, refreshStoredPermitAsync, isNewPermit } = baseFormManager;

    const startDateRef = useRef<HTMLInputElement>(null);
    const endDateRef = useRef<HTMLInputElement>(null);

    const startDateHandler = useDateInput({
        date: baseFormManager.formState.startDate.value,
        ref: startDateRef,
        comparer: Date_isSameDateAndTime,
        toStringValue: Date_toDateTimeString,
        onDateChange: startDateChangeHandler
    });

    const endDateHandler = useDateInput({
        date: baseFormManager.formState.endDate.value,
        ref: endDateRef,
        comparer: Date_isSameDateAndTime,
        toStringValue: Date_toDateTimeString,
        onDateChange: endDateChangeHandler
    });

    function startDateChangeHandler(updatedStartDate: Date) {
        const updatedEndDate =
            formState.endDate.value < updatedStartDate ? new Date(updatedStartDate) : new Date(formState.endDate.value);

        baseFormManager.registerFormStateChange((prevValue) => ({
            ...prevValue,
            startDate: { ...prevValue.startDate, value: updatedStartDate },
            endDate: { ...prevValue.endDate, value: updatedEndDate }
        }));
    }

    function endDateChangeHandler(updatedEndDate: Date) {
        const startDate =
            formState.startDate.value > updatedEndDate ? new Date(updatedEndDate) : new Date(formState.startDate.value);

        baseFormManager.registerFormStateChange((prevValue) => ({
            ...prevValue,
            startDate: { ...prevValue.startDate, value: startDate },
            endDate: { ...prevValue.endDate, value: updatedEndDate }
        }));
    }

    async function updateAdminCommentButtonClickHandlerAsync() {
        if (!storedPermit) {
            console.error("Tried to update the admin comment with no stored permit.");
            return;
        }

        const updateResult = await updateAdminCommentAsync(
            storedPermit.entityId,
            formState.adminComment.value,
            props.permitType
        );

        if (updateResult.failed) {
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("RefreshPageAndTryAgain"));
            return;
        }

        showSuccessAlert(props.resources.get("AdminCommentUpdated"));
    }

    const permitProviderSignature = storedPermit
        ? storedPermit.permitProviderSignature
        : Signature_empty(SignatureType.PermitProvider);

    const permitProviderSignatureManager = useSignature({
        baseUserInfo: props.baseUserInfo,
        signature: permitProviderSignature,
        onRemoveSignature: async () => await baseFormManager.removeSignatureManagerAsync(permitProviderSignature),
        onSaveSignature: async (signatureBlob, nameClarification) =>
            await baseFormManager.createSignatureManagerAsync(
                signatureBlob,
                nameClarification,
                SignatureType.PermitProvider
            ),
        resources: props.resources,
        hideSignature: !!isNewPermit
    });

    async function updatePositionsClickHandlerAsync() {
        if (!storedPermit) {
            console.error("Tried to update the positions with no stored permit.");
            return;
        }

        const positions = formState.positions.map(PositionPickerRow_toPosition);

        const updateResult = await updatePositionsAsync(storedPermit.entityId, positions, props.permitType);

        if (updateResult.failed) {
            handleValidationErrors(updateResult.error);
            return;
        }

        showSuccessAlert(props.resources.get("PositionsUpdated"));
        await refreshStoredPermitAsync();
    }

    async function updateWithNotificationHandlerAsync(comment: string) {
        if (!storedPermit) {
            console.error("Tried to update without a stored permit");
            return;
        }

        const request = props.toRequest(formState);
        (request as AnonymousPermitRequest).updateNotificationComment = comment;

        baseFormManager.setRequestInProgress(true);
        const updateResult = await updatePermitAsync(storedPermit.entityId, request, props.permitType);
        baseFormManager.setRequestInProgress(false);

        if (updateResult.failed) {
            handleValidationErrors(updateResult.error);
            return;
        }

        setShowUpdateWithNotificationModal(false);
        showSuccessAlert(props.resources.get("WorkPermitUpdated"));
        await refreshStoredPermitAsync();
    }

    const showRejectionComment =
        !!storedPermit && storedPermit.status === CodeStatus.Rejected && !!storedPermit.rejectionComment;

    const submitHandler = baseFormManager.canCreate
        ? baseFormManager.submitCreateHandlerAsync
        : baseFormManager.canUpdate
          ? baseFormManager.submitUpdateHandlerAsync
          : undefined;

    const canManagePositions = !!isNewPermit || (!!storedPermit && storedPermit.canManagePositions);

    const showUpdatePositionsButton =
        !!storedPermit && storedPermit.status === CodeStatus.Accepted && canManagePositions;

    const canManageAdminComment = !!storedPermit && storedPermit.canManageAdminComment;
    const showAdminComment = !!storedPermit && (!!storedPermit.adminComment || storedPermit.canManageAdminComment);
    const showUpdateAdminCommentButton =
        !!storedPermit && storedPermit.status === CodeStatus.Accepted && storedPermit.canManageAdminComment;

    return {
        startDateRef,
        endDateRef,
        startDateHandler,
        endDateHandler,
        permitProviderSignatureManager,
        showRejectionComment,
        canManagePositions,
        showUpdatePositionsButton,
        showAdminComment,
        canManageAdminComment,
        showUpdateAdminCommentButton,
        showUpdateWithNotificationModal,

        submitHandler,
        updatePositionsClickHandlerAsync,
        updateAdminCommentButtonClickHandlerAsync,
        setShowUpdateWithNotificationModal,
        updateWithNotificationHandlerAsync,

        formState: baseFormManager.formState,
        isNewPermit: baseFormManager.isNewPermit,
        storedPermit: baseFormManager.storedPermit,
        department: baseFormManager.department,
        requestInProgress: baseFormManager.requestInProgress,
        canCreate: baseFormManager.canCreate,
        canUpdate: baseFormManager.canUpdate,
        showRejectPermitModal: baseFormManager.showRejectPermitModal,
        isLoading: baseFormManager.isLoading,
        showAcceptUnsavedChangesModal: baseFormManager.showAcceptUnsavedChangesModal,
        canManageContent: baseFormManager.canManageContent,
        canManagePositionPolygons: baseFormManager.canManagePositionPolygons,
        contentVersion: baseFormManager.contentVersion,
        acceptButtonClickHandler: baseFormManager.acceptButtonClickHandler,
        copyButtonClickHandler: baseFormManager.copyButtonClickHandler,
        rejectPermitClickHandler: baseFormManager.rejectPermitClickHandler,
        addPositionPickerRowHandler: baseFormManager.addPositionPickerRowHandler,
        updatePositionPickerRowHandler: baseFormManager.updatePositionPickerRowHandler,
        removePositionPickerRowHandler: baseFormManager.removePositionPickerRowHandler,
        formStateChangeHandler: baseFormManager.formStateChangeHandler,
        personFormElementChangeHandler: baseFormManager.personFormElementChangeHandler,
        setShowRejectPermitModal: baseFormManager.setShowRejectPermitModal,
        rejectPermitHandlerAsync: baseFormManager.rejectPermitHandlerAsync,
        registerFormStateChange: baseFormManager.registerFormStateChange,
        createHandlerAsync: baseFormManager.createHandlerAsync,
        renewPermitClickHandlerAsync: baseFormManager.renewPermitClickHandlerAsync,
        setShowAcceptUnsavedChangesModal: baseFormManager.setShowAcceptUnsavedChangesModal,
        unsavedChangesModalAcceptHandlerAsync: baseFormManager.unsavedChangesModalAcceptHandlerAsync,
        unsavedChangesModalUpdateHandlerAsync: baseFormManager.unsavedChangesModalUpdateHandlerAsync,
        handleValidationErrors: baseFormManager.handleValidationErrors,
        setFormState: baseFormManager.setFormState,
        addParticipantHandler: baseFormManager.addParticipantHandler,
        removeParticipantHandler: baseFormManager.removeParticipantHandler,
        updateParticipantHandler: baseFormManager.updateParticipantHandler,
        refreshStoredPermitAsync: baseFormManager.refreshStoredPermitAsync
    };
}

export default usePortControlPermitForm;
